import { useCallback, useEffect, useRef } from 'react';
import { useGardenSettings } from '../context/GardenSettingsContext';
import { usePlant } from '../context/PlantContext';
import { gardensApi, imagesApi } from '../services/api';
import useGardenLoader from './useGardenLoader';
import {
  mapBackendToFrontend,
  mapFrontendToBackend,
  sanitizeBackgroundImage,
} from '../lib/gardenMappers';
import { isPersistableObject } from '../components/Editor/utils/persistableObjects';

// Re-export pure functions so existing callsites (tests, MainLayout, etc.) don't break
export { mapBackendToFrontend, mapFrontendToBackend };

// Auto-save interval in ms
const AUTO_SAVE_INTERVAL = 30000;

/**
 * Convert a data URL to a File object for upload.
 * Handles the case where the File reference was lost (e.g., page refresh
 * restored dataUrl from localStorage but File can't be serialized).
 */
function dataUrlToFile(dataUrl, _filename = 'background.png') {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const ext = mime === 'image/jpeg' ? '.jpg' : '.png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], `background${ext}`, { type: mime });
}

/**
 * Upload background image if needed.
 * - Has imageUrl and no new file → just sanitize (no re-upload)
 * - Has file → upload file, return imageUrl
 * - Has dataUrl but no file → convert to File, upload, return imageUrl
 * - Nothing → return undefined (skip backgroundImage in payload)
 */
export async function uploadBackgroundImageIfNeeded(bgImage) {
  if (!bgImage) return undefined;

  // Already uploaded (has imageUrl, no new file) → just sanitize
  if (bgImage.imageUrl && !bgImage.file) {
    return sanitizeBackgroundImage(bgImage);
  }

  // New image (has file) → upload first
  if (bgImage.file) {
    const result = await imagesApi.upload(bgImage.file);
    return {
      ...sanitizeBackgroundImage(bgImage),
      imageUrl: result.imageUrl,
    };
  }

  // Has dataUrl but no file (e.g., File lost after localStorage round-trip)
  // Convert dataUrl to File and upload
  if (bgImage.dataUrl) {
    const file = dataUrlToFile(bgImage.dataUrl);
    const result = await imagesApi.upload(file);
    return {
      ...sanitizeBackgroundImage(bgImage),
      imageUrl: result.imageUrl,
    };
  }

  return undefined;
}

/**
 * Serialize canvas objects for backend storage.
 *
 * Uses the same exclusion set as the localStorage path (`isPersistableObject`).
 * It used to filter a strict subset of it, which let an auto-save that ran
 * during vertex edit mode persist the live handles — they returned as inert,
 * selectable circles and stacked up one generation per save/load cycle (#683).
 */
export function serializeCanvasObjects(canvas) {
  if (!canvas) return null;

  const canvasObjects = canvas.getObjects();
  const objectsToSave = canvasObjects.filter(isPersistableObject);

  if (objectsToSave.length === 0) return null;

  return {
    version: '6.0.0',
    objects: objectsToSave.map(obj => {
      if (obj.objectType === 'plant') {
        const plantJson = obj.toObject();
        if (plantJson.data?.isActive) {
          plantJson.data.isActive = false;
        }
        delete plantJson.isOverlapping;
        delete plantJson.overlapState;
        return plantJson;
      }

      const objJson = obj.toJSON(['data', 'objectType', 'renderOptions']);
      if (obj.data) objJson.data = obj.data;
      if (obj.objectType) objJson.objectType = obj.objectType;
      if (obj.renderOptions) objJson.renderOptions = obj.renderOptions;
      if (objJson.data?.isActive) {
        objJson.data.isActive = false;
      }
      return objJson;
    }),
  };
}

/**
 * useGardenPersistence - Hook for saving/loading gardens to/from backend
 *
 * @param {React.RefObject} fabricCanvasRef - Ref to the Fabric.js canvas instance
 * @returns {{ saveToBackend, loadFromBackend, saveStatus }}
 */
export default function useGardenPersistence(fabricCanvasRef) {
  const {
    settings,
    isConfigured,
    gardenId,
    setGardenId,
    gardenVersion,
    setGardenVersion,
    saveStatus,
    setSaveStatus,
    isDirty,
    setIsDirty,
    syncCanvasSnapshot,
  } = useGardenSettings();

  const { favorites } = usePlant();

  // Delegate load path to the slim hook (no canvas ref, no auto-save interval)
  const { loadFromBackend } = useGardenLoader();

  // Refs for auto-save to avoid stale closures
  const settingsRef = useRef(settings);
  const favoritesRef = useRef(favorites);
  const gardenIdRef = useRef(gardenId);
  const isDirtyRef = useRef(isDirty);
  const versionRef = useRef(gardenVersion);

  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { favoritesRef.current = favorites; }, [favorites]);
  useEffect(() => { gardenIdRef.current = gardenId; }, [gardenId]);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => { versionRef.current = gardenVersion; }, [gardenVersion]);

  // In-flight guard (#496): prevents two concurrent PATCHes on the same garden
  // from one tab. While a save runs, further requests set pendingSaveRef; the
  // guard then re-runs the save once with the latest canvas state (a superset),
  // so no objects are lost.
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  // Canvas JSON a queued save must use instead of re-reading the canvas (#680).
  // Set when the editor unmounts while a save is in flight — by the time the
  // queue drains, the Fabric canvas is disposed and can no longer be read.
  const pendingCanvasJsonRef = useRef(undefined);

  // Session-lost guard (#679): a 401 means the session cookie is gone or
  // expired — no amount of retrying fixes that, only a reload/login does.
  // Without this flag the 30s auto-save hammered the same doomed PATCH
  // indefinitely while the toast claimed "wird erneut versucht".
  const sessionLostRef = useRef(false);

  /**
   * Perform a single save (POST for new, PATCH for existing). Always reads the
   * latest state from refs, so a queued re-run captures the newest canvas.
   * Callers must go through saveToBackend, which serializes invocations.
   *
   * @param {object}  [options]
   * @param {boolean} [options.silent]    - Suppress the 'saving' status
   * @param {object}  [options.canvasJson] - Pre-serialized canvas to send instead
   *   of reading the live canvas. Used by the unmount flush (#680), which has to
   *   serialize while the Fabric canvas is still alive.
   */
  const performSave = useCallback(async ({ silent = false, canvasJson } = {}) => {
    const currentSettings = settingsRef.current;
    const currentFavorites = favoritesRef.current;
    const currentGardenId = gardenIdRef.current;

    // Serialize canvas objects (unless the caller already did — see canvasJson)
    const canvasObjects = canvasJson !== undefined
      ? canvasJson
      : serializeCanvasObjects(fabricCanvasRef?.current);

    // Upload background image if needed (file or dataUrl → POST /images/upload)
    // Always process backgroundImage to ensure imageUrl is set before sending to backend
    let settingsForBackend = currentSettings;
    if (currentSettings.backgroundImage && !currentSettings.backgroundImage.imageUrl) {
      try {
        const uploadedBgImage = await uploadBackgroundImageIfNeeded(currentSettings.backgroundImage);
        settingsForBackend = { ...currentSettings, backgroundImage: uploadedBgImage };
      } catch (uploadError) {
        console.error('Failed to upload background image:', uploadError.message);
        setSaveStatus('error');
        return;
      }
    }

    // Build backend payload
    const payload = mapFrontendToBackend(settingsForBackend, canvasObjects, currentFavorites);

    if (!silent) setSaveStatus('saving');

    try {
      if (currentGardenId) {
        // PATCH existing garden — send If-Match for optimistic locking (#496)
        const updated = await gardensApi.patch(currentGardenId, payload, {
          version: versionRef.current,
        });
        // Sync the ref synchronously so a queued re-run uses the fresh version
        // (avoids a 412 against our own just-completed write).
        if (updated?.version != null) {
          versionRef.current = updated.version;
          setGardenVersion(updated.version);
        }
      } else {
        // POST new garden
        const created = await gardensApi.create(payload);
        // Update both ref and state synchronously so a queued re-run PATCHes
        // the new garden instead of creating a duplicate.
        gardenIdRef.current = String(created.id);
        setGardenId(String(created.id));
        if (created?.version != null) {
          versionRef.current = created.version;
          setGardenVersion(created.version);
        }
      }
      sessionLostRef.current = false;
      // Keep the context snapshot in step with what the server now holds (#680).
      // The editor unmounts on every dashboard visit and re-seeds from this
      // value; without the sync it stayed at the page-load state and the save
      // we just made would be invisible on return. Skipped when canvasObjects
      // is null — that means "canvas empty, field omitted from the payload", so
      // the server copy is unchanged and overwriting the seed with null would
      // drop the editor back to the localStorage cache for no reason.
      if (canvasObjects) syncCanvasSnapshot(canvasObjects);
      setSaveStatus('saved');
      setIsDirty(false);

      // Reset status back to idle after 2s
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      if (error.status === 401) {
        // Session cookie expired or missing (#679). The garden is untouched on
        // the server and the local copy is still in localStorage — but this
        // browser can no longer prove it owns the garden. Stop the auto-save
        // loop and say so, instead of retrying every 30s behind a toast that
        // promises a retry which can never succeed.
        console.warn('Save rejected (401): session expired — stopping auto-save.');
        sessionLostRef.current = true;
        setSaveStatus('session-expired');
        return;
      }
      if (error.status === 412) {
        // Optimistic-lock conflict: another tab/device saved in the meantime.
        // Re-sync our version from the server so a later user-initiated save can
        // proceed; do NOT silently overwrite. localStorage retains the data.
        console.warn(
          'Stale write (412): garden was modified elsewhere. Re-syncing version.'
        );
        try {
          const fresh = await gardensApi.getById(currentGardenId);
          if (fresh?.version != null) {
            versionRef.current = fresh.version;
            setGardenVersion(fresh.version);
          }
        } catch (refreshError) {
          console.warn('Failed to refresh garden version after 412:', refreshError.message);
        }
        setSaveStatus('error');
        return;
      }
      console.error('Failed to save garden to backend:', error.message);
      console.error('Payload sent:', JSON.stringify(payload, null, 2).substring(0, 2000));
      setSaveStatus('error');
      // localStorage remains as fallback (handled by GardenCanvas debounced save)
    }
  }, [fabricCanvasRef, setGardenId, setGardenVersion, setSaveStatus, setIsDirty, syncCanvasSnapshot]);

  /**
   * Save garden to backend with an in-flight guard (#496).
   *
   * If a save is already running, the request is recorded as pending and the
   * function returns immediately — no second concurrent PATCH is issued. When
   * the running save finishes, any pending request is drained once with the
   * then-current canvas state.
   */
  const saveToBackend = useCallback(async ({ silent = false, canvasJson } = {}) => {
    if (!isConfigured) {
      console.warn('Cannot save: Garden not configured (wizard incomplete)');
      return;
    }

    if (isSavingRef.current) {
      pendingSaveRef.current = true;
      // A pre-serialized canvas has to survive into the drained run — the live
      // canvas it came from may be gone by then (#680). Last one wins; it is
      // always the freshest snapshot.
      if (canvasJson !== undefined) pendingCanvasJsonRef.current = canvasJson;
      return;
    }

    isSavingRef.current = true;
    try {
      await performSave({ silent, canvasJson });
      // Drain saves requested while this one was in flight (latest state wins).
      while (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        const queuedCanvasJson = pendingCanvasJsonRef.current;
        pendingCanvasJsonRef.current = undefined;
        await performSave({ silent: true, canvasJson: queuedCanvasJson });
      }
    } finally {
      isSavingRef.current = false;
    }
  }, [isConfigured, performSave]);

  /**
   * Flush unsaved changes when the editor is torn down (#680).
   *
   * Every exit from the editor unmounts MainLayout — the desktop header button,
   * the mobile bottom tabs, a resize across the mobile breakpoint. Without this,
   * edits made in the last <30s (between the change and the next auto-save tick)
   * only survived in localStorage, and the returning editor seeded itself from
   * the backend snapshot instead, so they looked lost.
   *
   * Must be called by GardenCanvas's cleanup *before* it disposes the Fabric
   * canvas — this reads it synchronously, and a disposed canvas reports zero
   * objects. The snapshot is then handed to saveToBackend rather than re-read
   * later, so a run queued behind an in-flight save still sends live data.
   *
   * Deliberately does nothing for an empty canvas: the only way to legitimately
   * reach zero objects is deleting them, and handleObjectDelete already flushes
   * each deletion immediately. That makes "empty" a strong signal for a disposed
   * or not-yet-loaded canvas — the one input that must never reach a PATCH.
   * (mapFrontendToBackend would omit the field and the server would keep its
   * copy, so this is a second line of defence, not the only one.)
   */
  const flushOnUnmount = useCallback(() => {
    if (!isDirtyRef.current || !gardenIdRef.current) return;
    if (sessionLostRef.current) return;
    const canvasJson = serializeCanvasObjects(fabricCanvasRef?.current);
    if (!canvasJson?.objects?.length) return;

    // Seed the next mount right away, not when the PATCH resolves. A user who
    // returns to the editor within the round-trip would otherwise be re-seeded
    // from the pre-flush snapshot — the very symptom this fixes. Should the
    // PATCH fail, the snapshot is ahead of the server, which matches what the
    // user actually has; isDirty stays set, so the next save retries.
    syncCanvasSnapshot(canvasJson);
    saveToBackend({ silent: true, canvasJson });
  }, [fabricCanvasRef, saveToBackend, syncCanvasSnapshot]);

  /**
   * Auto-save: every 30s if dirty and gardenId exists (no automatic POST for new gardens).
   *
   * Paused once a save came back 401 (#679) — the session is gone and every
   * further attempt would fail identically. A manual save still goes through,
   * so a user who re-authenticated in another tab can retry deliberately.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (isDirtyRef.current && gardenIdRef.current && !sessionLostRef.current) {
        saveToBackend({ silent: true });
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(interval);
  }, [saveToBackend]);

  /**
   * Unload flush (#482): when the page is being closed/navigated away and there
   * are unsaved changes, push them via a keepalive PATCH. This closes the
   * data-loss window between an edit and the next 30s auto-save now that the
   * backend is the authoritative reload source (#484) — localStorage is no
   * longer replayed on reload, so an unsaved edit only survives if it reached
   * the backend first.
   *
   * Fires on `pagehide` and `visibilitychange`→hidden, NOT `beforeunload`:
   * `visibilitychange`→hidden fires while the document is still alive, so the
   * beacon is queued reliably; `pagehide` covers the cases where the visibility
   * transition is skipped. This is the Page Lifecycle best-practice terminal-save
   * point. Transport is `navigator.sendBeacon` (via gardensApi.flushBeacon) —
   * a keepalive `fetch` here is torn down by Firefox and lost.
   *
   * Deduped to a single beacon per hide (`sent`), reset when the page becomes
   * visible again so a later reload/close flushes fresh edits. Best-effort and
   * fire-and-forget. Only flushes existing gardens — never an auto-POST for an
   * unsaved one. The background image is intentionally not (re-)uploaded here;
   * sanitizeBackgroundImage (inside mapFrontendToBackend) drops the un-uploaded
   * file/dataUrl so the payload stays small and valid.
   */
  useEffect(() => {
    let sent = false;

    const flush = () => {
      if (sent) return;
      if (!isDirtyRef.current || !gardenIdRef.current) return;
      // A lost session (#679) would only produce another 401 on the way out.
      if (sessionLostRef.current) return;
      const canvasObjects = serializeCanvasObjects(fabricCanvasRef?.current);
      const payload = mapFrontendToBackend(
        settingsRef.current,
        canvasObjects,
        favoritesRef.current
      );
      sent = true;
      gardensApi.flushBeacon(gardenIdRef.current, payload);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush();
      } else {
        // Back in view: allow a future hide/close to flush fresh edits.
        sent = false;
      }
    };

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fabricCanvasRef]);

  return {
    saveToBackend,
    flushOnUnmount,
    loadFromBackend,
    saveStatus,
  };
}
